import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ComposedChart, Bar, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { toast } from 'src/@/components/ui/use-toast';
import { CHART_TOOLTIP_STYLE } from 'src/design/tokens';
import mktAnunciosService from 'src/services/mktAnunciosService';
import MktPageHeader from 'src/sections/@dashboard/marketing/components/MktPageHeader';
import StatusChip from 'src/sections/@dashboard/marketing/components/StatusChip';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import { MKT_ADS_DESIGN as d } from './MktAds.design';
import {
    getLevelLabel,
    getQuickPresetOptions,
    SPLIT_REQUESTS,
    aggregateInsightsBy,
    creationDate,
    formatDate,
    adResult,
    resultLabel,
    costPerResult,
    frequency,
    budgetInfo,
    adsManagerUrl,
    statusInfo,
    formatCurrency,
    formatInt,
    formatPercent,
    formatDecimal
} from './constants';

const BASE_URL = '/dashboard/marketing/anuncios';

function dailyResultValue(row, source) {
    if (!source) return 0;
    if (source === 'reach') return Number(row.reach) || 0;
    if (source === 'clicks') return Number(row.clicks) || 0;
    return Number(row.actions?.[source]) || 0;
}

function formatDayTick(value) {
    if (!value) return '';
    const [y, m, day] = String(value).split('-');
    if (!day) return value;
    return new Date(Number(y), Number(m) - 1, Number(day))
        .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

const UNKNOWN_DIMENSION = /^(unknown|undetermined|uncategorized)$/i;

export default function MktAdPage({ adId }) {
    const { t, i18n } = useTranslation('marketing');
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Voltar pelo historico preserva os filtros da listagem (que vivem na
    // query string); BASE_URL seco e so o fallback de deep link.
    const goBack = () => {
        if (location.key !== 'default') navigate(-1);
        else navigate(BASE_URL);
    };
    const [ad, setAd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingInsights, setLoadingInsights] = useState(false);
    const [datePreset, setDatePreset] = useState('last_30d');
    const [aggregate, setAggregate] = useState(null);
    const [daily, setDaily] = useState([]);
    const [splitRows, setSplitRows] = useState({});
    const [activeSplit, setActiveSplit] = useState('publisher_platform');
    const [children, setChildren] = useState([]);

    const levelLabel = useMemo(() => getLevelLabel(t), [t]);
    const quickPresets = useMemo(
        () => getQuickPresetOptions(t).filter((p) => p.value !== 'today'),
        [t]
    );

    // Moeda de exibicao segue o idioma da UI (Real/pt-BR, Dolar/en), nao a
    // moeda real da conta — ver formatCurrency em constants.js.
    const currency = i18n.language === 'en' ? 'USD' : 'BRL';

    const loadAd = useCallback(async () => {
        setLoading(true);
        try {
            const res = await mktAnunciosService.buscar(adId);
            setAd(res?.data || null);
            setAggregate(res?.data?.insights || null);
        } catch (err) {
            toast({
                title: t('common.error'),
                description: t('ads.detail.loadFailed'),
                variant: 'destructive'
            });
            navigate(BASE_URL);
        } finally {
            setLoading(false);
        }
    }, [adId, navigate, t]);

    useEffect(() => {
        loadAd();
    }, [loadAd]);

    // Agregado, serie diaria e as divisoes vem do mesmo periodo, entao sao
    // pedidos juntos — a tela inteira reflete um recorte so. Cada divisao falha
    // isolada (`.catch`), pra uma combinacao recusada pela Meta nao derrubar o
    // resto do card. Trocar de tab de divisao nao dispara request: tudo ja
    // chegou nesta carga.
    const loadInsights = useCallback(async () => {
        if (!adId) return;
        setLoadingInsights(true);
        try {
            const [agg, series, ...splits] = await Promise.all([
                mktAnunciosService.insights(adId, { date_preset: datePreset }),
                mktAnunciosService.insights(adId, { date_preset: datePreset, time_increment: 1 }).catch(() => null),
                ...SPLIT_REQUESTS.map((request) => mktAnunciosService
                    .insights(adId, { date_preset: datePreset, breakdowns: request.breakdowns })
                    .catch(() => null))
            ]);

            const aggRow = agg?.data?.rows?.[0] || null;
            setAggregate(aggRow);
            if (aggRow) setAd((prev) => (prev ? { ...prev, insights: aggRow } : prev));
            setDaily(series?.data?.rows || []);
            setSplitRows(SPLIT_REQUESTS.reduce((acc, request, index) => ({
                ...acc,
                [request.id]: splits[index]?.data?.rows || []
            }), {}));
        } catch (err) {
            if (err?.kind === 'MISSING_SCOPE') {
                toast({
                    title: t('ads.detail.insufficientScope'),
                    description: t('ads.detail.insufficientScopeDesc'),
                    variant: 'destructive'
                });
            } else {
                toast({
                    title: t('ads.detail.loadInsightsFailed'),
                    description: err?.message,
                    variant: 'destructive'
                });
            }
        } finally {
            setLoadingInsights(false);
        }
    }, [adId, datePreset, t]);

    useEffect(() => {
        if (ad) loadInsights();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ad?._id, datePreset]);

    // Filhos diretos: conjuntos de uma campanha, anuncios de um conjunto.
    // Depende dos campos, nao do objeto `ad`: cada carga de insights troca a
    // identidade dele e refaria esta busca sem necessidade.
    const adLevel = ad?.level;
    const adExternalId = ad?.external_id;
    useEffect(() => {
        if (!adExternalId || adLevel === 'ad' || !user?.clienteId) {
            setChildren([]);
            return;
        }
        const query = adLevel === 'campaign'
            ? { level: 'adset', campaign_id: adExternalId }
            : { level: 'ad', adset_id: adExternalId };
        mktAnunciosService.listar(user.clienteId, query)
            .then((res) => setChildren(res?.data || []))
            .catch(() => setChildren([]));
    }, [adLevel, adExternalId, user?.clienteId]);

    if (loading) {
        return (
            <div className={d.page.content}>
                <MktLoading text={t('ads.detail.loading')} />
            </div>
        );
    }

    if (!ad) return null;

    const insights = aggregate || ad.insights || {};
    const status = statusInfo(ad.effective_status || ad.status, t);
    const account = ad.account_id;
    const result = adResult({ ...ad, insights });
    const resultName = resultLabel(result.labelKey, t) || t('ads.results.results');
    const cpr = costPerResult({ ...ad, insights }, result);
    const freq = frequency(insights);
    const budget = budgetInfo(ad, currency, t);
    const managerUrl = adsManagerUrl(ad);

    const spend = Number(insights.spend) || 0;
    const impressions = Number(insights.impressions) || 0;
    const clicks = Number(insights.clicks) || 0;
    // Taxa clique→resultado so faz sentido quando o resultado e uma acao de
    // verdade — com alcance/cliques como "resultado" ela seria circular.
    const resultIsAction = Boolean(result.source) && result.source !== 'reach' && result.source !== 'clicks';
    const convRate = resultIsAction && clicks > 0 && result.value !== null
        ? (result.value / clicks) * 100
        : null;

    const dimLabel = (key, value) => (key === 'age'
        ? String(value)
        : t(`ads.dim.${String(value).toLowerCase()}`, { defaultValue: String(value) }));

    // ------------------------------------------------------------------
    // Divisoes: uma view por dimensao, todas ja carregadas; tabs so trocam
    // qual view esta visivel.
    // ------------------------------------------------------------------
    const splitViews = SPLIT_REQUESTS
        .flatMap((request) => request.sections.map((key) => ({
            key,
            label: t(`ads.breakdown.${key}`, { defaultValue: key }),
            rows: aggregateInsightsBy(splitRows[request.id], key)
        })))
        .filter((view) => view.rows.length > 0);

    const currentView = splitViews.find((view) => view.key === activeSplit) || splitViews[0] || null;

    const aggCtr = Number(insights.ctr) || (impressions > 0 ? (clicks / impressions) * 100 : 0);

    const buildSplitRow = (view, row) => {
        const rowResult = dailyResultValue(row, result.source);
        const share = spend > 0 ? (row.spend / spend) * 100 : 0;
        const isAttribution = (view.key === 'age' || view.key === 'gender')
            && UNKNOWN_DIMENSION.test(String(row.dimension));
        const converted = resultIsAction && rowResult > 0;
        const dead = resultIsAction && rowResult === 0 && share >= 5;
        const flag = isAttribution
            ? { label: t('ads.split.flag.attribution'), tone: 'warning' }
            : converted
                ? { label: t('ads.split.flag.converted'), tone: 'success' }
                : dead
                    ? { label: t('ads.split.flag.noResult'), tone: 'danger' }
                    : { label: t('ads.split.flag.lowSpend'), tone: 'neutral' };
        return {
            ...row,
            label: dimLabel(view.key, row.dimension),
            rowResult,
            share,
            converted,
            dead,
            flag,
            barClass: converted
                ? 'bg-green-500'
                : dead
                    ? 'bg-red-300 dark:bg-red-500/40'
                    : 'bg-violet-200 dark:bg-violet-500/30',
            ctrLow: row.ctr !== null && aggCtr > 0 && row.ctr < aggCtr * 0.5
        };
    };

    const currentRows = currentView ? currentView.rows.map((row) => buildSplitRow(currentView, row)) : [];
    const maxSplitSpend = currentRows.reduce((acc, row) => Math.max(acc, row.spend), 0);
    const splitTotals = currentRows.reduce((acc, row) => ({
        spend: acc.spend + row.spend,
        results: acc.results + row.rowResult,
        impressions: acc.impressions + row.impressions,
        clicks: acc.clicks + row.clicks
    }), { spend: 0, results: 0, impressions: 0, clicks: 0 });
    const splitTotalCtr = splitTotals.impressions > 0
        ? (splitTotals.clicks / splitTotals.impressions) * 100
        : null;
    const deadSpend = currentRows.filter((row) => row.dead).reduce((acc, row) => acc + row.spend, 0);
    const hasAttributionRow = currentRows.some((row) => row.flag.tone === 'warning');

    // ------------------------------------------------------------------
    // Alertas: leituras derivadas dos dados carregados — nada de benchmark
    // externo ou meta configurada, que nao existem no modelo.
    // ------------------------------------------------------------------
    const alerts = [];
    if (result.value !== null && result.value > 0) {
        alerts.push({
            tone: 'brand',
            tag: t('ads.alerts.cprTag'),
            title: t('ads.alerts.cprTitle', { value: cpr !== null ? formatCurrency(cpr, currency) : '—', name: resultName }),
            body: t('ads.alerts.cprBody', {
                count: formatInt(result.value),
                name: resultName,
                spend: formatCurrency(spend, currency)
            })
        });
    } else if (spend > 0) {
        alerts.push({
            tone: 'danger',
            tag: t('ads.alerts.cprTag'),
            title: t('ads.alerts.cprTitleNone', { name: resultName }),
            body: t('ads.alerts.cprBodyNone', { spend: formatCurrency(spend, currency) })
        });
    }
    if (impressions > 0) {
        alerts.push({
            tone: clicks === 0 ? 'warning' : 'brand',
            tag: t('ads.alerts.clickTag'),
            title: t('ads.alerts.clickTitle', {
                ctr: formatPercent(insights.ctr),
                cpm: formatCurrency(insights.cpm, currency)
            }),
            body: t('ads.alerts.clickBody', { impressions: formatInt(impressions), clicks: formatInt(clicks) })
        });
    }
    const platformView = splitViews.find((view) => view.key === 'publisher_platform');
    if (platformView && spend > 0 && resultIsAction && result.value > 0) {
        const platformRows = platformView.rows.map((row) => buildSplitRow(platformView, row));
        const platformDead = platformRows.filter((row) => row.dead).reduce((acc, row) => acc + row.spend, 0);
        const deadPct = (platformDead / spend) * 100;
        const top = platformRows.reduce(
            (acc, row) => (row.rowResult > (acc?.rowResult || 0) ? row : acc),
            null
        );
        if (deadPct >= 40 && top) {
            alerts.push({
                tone: deadPct >= 70 ? 'danger' : 'warning',
                tag: t('ads.alerts.spendTag'),
                title: t('ads.alerts.spendTitle', { value: formatCurrency(platformDead, currency) }),
                body: t('ads.alerts.spendBody', {
                    pct: Math.round(deadPct),
                    name: resultName,
                    top: top.label,
                    share: Math.round(top.share)
                })
            });
        }
    }

    // ------------------------------------------------------------------
    // Serie diaria: barras de gasto + marcador nos dias com resultado.
    // Uma escala so (gasto); o resultado e evento, nao segunda serie.
    // ------------------------------------------------------------------
    const dailySeries = daily
        .filter((row) => row.date_start)
        .map((row) => ({
            date: row.date_start,
            spend: Number(row.spend) || 0,
            results: dailyResultValue(row, result.source)
        }));
    const maxDailySpend = dailySeries.reduce((acc, row) => Math.max(acc, row.spend), 0);
    const dotY = maxDailySpend > 0 ? maxDailySpend * 1.12 : 1;
    const chartData = dailySeries.map((row) => ({
        ...row,
        dot: resultIsAction && row.results > 0 ? dotY : null
    }));

    const panels = [
        {
            title: t('ads.detail.panels.configuration'),
            items: [
                { k: t('ads.detail.fields.level'), v: levelLabel[ad.level] || ad.level },
                { k: t('ads.detail.fields.objective'), v: ad.objective ? t(`ads.objective.${ad.objective}`, { defaultValue: ad.objective }) : '—' },
                { k: t('ads.detail.fields.budget'), v: `${budget.value} ${budget.sub}`.trim() },
                { k: t('ads.detail.fields.adAccount'), v: account?.account_name || '—' },
                { k: t('ads.detail.fields.facebookPage'), v: ad.page_name || ad.page_id || '—' }
            ]
        },
        {
            title: t('ads.detail.panels.structure'),
            items: ad.level === 'ad'
                ? [
                    { k: t('ads.detail.fields.callToAction'), v: ad.creative?.call_to_action_type || '—' },
                    { k: t('ads.detail.fields.createdAt'), v: formatDate(creationDate(ad)) }
                ]
                : [
                    {
                        k: ad.level === 'campaign' ? t('ads.level.adset') : t('ads.level.ad'),
                        v: t('ads.detail.childrenCount', {
                            count: children.length,
                            delivering: children.filter((c) => Number(c.insights?.spend) > 0).length
                        })
                    },
                    { k: t('ads.detail.fields.createdAt'), v: formatDate(creationDate(ad)) }
                ]
        },
        {
            title: t('ads.detail.panels.technical'),
            muted: true,
            items: [
                { k: t('ads.detail.fields.metaId'), v: ad.external_id },
                { k: t('ads.detail.fields.campaign'), v: ad.parent_ids?.campaign_id || '—' },
                { k: t('ads.detail.fields.adset'), v: ad.parent_ids?.adset_id || '—' },
                { k: t('ads.detail.fields.apiObjective'), v: ad.objective || '—' }
            ]
        }
    ];

    return (
        <div className={d.page.content}>
            <MktPageHeader
                icon="logos:facebook"
                iconVariant="brand"
                title={ad.name || t('ads.table.noName')}
                subtitle={`${levelLabel[ad.level] || ad.level}${account?.account_name ? ` · ${account.account_name}` : ''}`}
                onBack={goBack}
                badges={(
                    <>
                        <StatusChip tone={status.tone} label={status.label} />
                        {ad.objective && (
                            <span className={d.adsTable.tag}>
                                {t(`ads.objective.${ad.objective}`, { defaultValue: ad.objective })}
                            </span>
                        )}
                        {ad.creative?.call_to_action_type && (
                            <span className={d.adsTable.tag}>{ad.creative.call_to_action_type}</span>
                        )}
                        {insights.date_start && (
                            <span className="text-xs text-muted-foreground">
                                {t('ads.detail.periodRange', { start: insights.date_start, stop: insights.date_stop })}
                            </span>
                        )}
                    </>
                )}
                actions={managerUrl && (
                    <a
                        href={managerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 h-9 px-3.5 bg-card border border-border hover:bg-accent text-foreground text-xs font-medium rounded-lg transition-all"
                    >
                        <Iconify icon="eva:external-link-outline" width={16} />
                        {t('ads.detail.openInManager')}
                    </a>
                )}
            />

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
                <div className="space-y-5 min-w-0">
                    {alerts.length > 0 && (
                        <div className={d.pageDetail.alertGrid}>
                            {alerts.map((alert) => (
                                <div
                                    key={alert.tag}
                                    className={`${d.pageDetail.alertCard} ${d.pageDetail.alertTone[alert.tone].edge}`}
                                >
                                    <div className={`${d.pageDetail.alertTag} ${d.pageDetail.alertTone[alert.tone].tag}`}>
                                        {alert.tag}
                                    </div>
                                    <div className={d.pageDetail.alertTitle}>{alert.title}</div>
                                    <div className={d.pageDetail.alertBody}>{alert.body}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className={d.pageDetail.card}>
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                            <h3 className={`${d.pageDetail.cardTitle} mb-0`}>
                                <Iconify icon="eva:funnel-outline" width={18} className="text-violet-600 dark:text-violet-400" />
                                {t('ads.funnel.title')}
                                {loadingInsights && <Iconify icon="eos-icons:loading" width={16} className="text-muted-foreground" />}
                            </h3>
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-xs text-muted-foreground">
                                    {t('ads.funnel.invested', { value: formatCurrency(spend, currency) })}
                                    {' · '}
                                    {`${budget.value} ${budget.sub}`.trim()}
                                </span>
                                <div className={d.segmented.container}>
                                    {quickPresets.map((preset) => (
                                        <button
                                            key={preset.value}
                                            type="button"
                                            className={datePreset === preset.value ? d.segmented.itemActive : d.segmented.item}
                                            onClick={() => setDatePreset(preset.value)}
                                        >
                                            {preset.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className={d.pageDetail.funnelGrid}>
                            <div className={d.pageDetail.funnelCard}>
                                <div className={d.pageDetail.funnelLabel}>{t('ads.metrics.impressions')}</div>
                                <div className={d.pageDetail.funnelValue}>{formatInt(impressions)}</div>
                                <div className={d.pageDetail.funnelSub}>
                                    {t('ads.funnel.people', { value: formatInt(insights.reach) })}
                                    {freq !== null && ` · ${t('ads.kpi.frequencyValue', { value: formatDecimal(freq, 1) })}`}
                                </div>
                                <div className={d.pageDetail.funnelSub}>
                                    {t('ads.detail.cpmValue', { value: formatCurrency(insights.cpm, currency) })}
                                </div>
                            </div>

                            <div className={d.pageDetail.funnelConnector}>
                                <div className={d.pageDetail.funnelRate}>{formatPercent(insights.ctr)}</div>
                                <div className={d.pageDetail.funnelRateLine} />
                                <div className={d.pageDetail.funnelRateLabel}>{t('ads.funnel.clicked')}</div>
                            </div>

                            <div className={d.pageDetail.funnelCard}>
                                <div className={d.pageDetail.funnelLabel}>{t('ads.metrics.clicks')}</div>
                                <div className={d.pageDetail.funnelValue}>{formatInt(clicks)}</div>
                                <div className={d.pageDetail.funnelSub}>
                                    {t('ads.detail.cpcValue', { value: formatCurrency(insights.cpc, currency) })}
                                </div>
                                <div className={d.pageDetail.funnelSub}>
                                    {t('ads.kpi.ctrValue', { value: formatPercent(insights.ctr) })}
                                </div>
                            </div>

                            <div className={d.pageDetail.funnelConnector}>
                                <div className={d.pageDetail.funnelRate}>
                                    {convRate !== null ? formatPercent(convRate) : '—'}
                                </div>
                                <div className={d.pageDetail.funnelRateLine} />
                                <div className={d.pageDetail.funnelRateLabel}>{t('ads.funnel.converted')}</div>
                            </div>

                            <div
                                className={`${d.pageDetail.funnelCard} ${result.value ? d.pageDetail.funnelCardResult : spend > 0 ? d.pageDetail.funnelCardDead : ''}`}
                            >
                                <div className={d.pageDetail.funnelLabel}>{resultName}</div>
                                <div className={d.pageDetail.funnelValue}>
                                    {result.value !== null ? formatInt(result.value) : '—'}
                                </div>
                                <div className={d.pageDetail.funnelSub}>
                                    {cpr !== null
                                        ? `${formatCurrency(cpr, currency)} ${t('ads.detail.perResult', { name: resultName })}`
                                        : t('ads.split.noResultShort')}
                                </div>
                            </div>
                        </div>

                        {chartData.length > 1 && (
                            <div className="mt-5">
                                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                                    <span className="text-xs font-medium text-foreground">{t('ads.chartCard.spendPerDay')}</span>
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2 h-2 rounded-sm bg-[#C4B5FD]" />
                                            {t('ads.chartCard.legendSpend')}
                                        </span>
                                        {resultIsAction && (
                                            <span className="flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-green-600" />
                                                {t('ads.chartCard.dayWith', { name: resultName })}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className={d.pageDetail.chartRow}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                                            <XAxis
                                                dataKey="date"
                                                tickFormatter={formatDayTick}
                                                tickLine={false}
                                                axisLine={false}
                                                minTickGap={40}
                                                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                            />
                                            <YAxis hide domain={[0, Math.max(dotY * 1.08, 1)]} />
                                            <Tooltip
                                                {...CHART_TOOLTIP_STYLE}
                                                cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }}
                                                formatter={(value, name, props) => (name === 'dot'
                                                    ? [formatInt(props?.payload?.results), resultName]
                                                    : [formatCurrency(value, currency), t('ads.metrics.spend')])}
                                                labelFormatter={formatDayTick}
                                            />
                                            <Bar dataKey="spend" radius={[4, 4, 0, 0]} maxBarSize={18}>
                                                {chartData.map((row) => (
                                                    <Cell
                                                        key={row.date}
                                                        fill={row.dot ? '#8B5CF6' : '#C4B5FD'}
                                                    />
                                                ))}
                                            </Bar>
                                            <Scatter dataKey="dot" fill="#15803D" />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}
                    </div>

                    {currentView && (
                        <div className={d.surface.card + ' overflow-hidden'}>
                            <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-3.5">
                                <h3 className={`${d.pageDetail.cardTitle} mb-0`}>
                                    <Iconify icon="eva:pie-chart-outline" width={18} className="text-violet-600 dark:text-violet-400" />
                                    {t('ads.split.title')}
                                </h3>
                                <div className={d.segmented.container}>
                                    {splitViews.map((view) => (
                                        <button
                                            key={view.key}
                                            type="button"
                                            className={view.key === currentView.key ? d.segmented.itemActive : d.segmented.item}
                                            onClick={() => setActiveSplit(view.key)}
                                        >
                                            {view.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <div className={`grid ${d.pageDetail.splitCols} ${d.pageDetail.splitMinW} items-center gap-3 px-5 py-2.5 bg-muted/40 border-y border-border text-[11px] font-semibold uppercase tracking-wider text-muted-foreground`}>
                                    <div>{currentView.label}</div>
                                    <div>{t('ads.split.share')}</div>
                                    <div className="text-right">{resultName}</div>
                                    <div className="text-right">{t('ads.table.costPerResult')}</div>
                                    <div className="text-right">{t('ads.metrics.ctr')}</div>
                                </div>

                                {currentRows.map((row) => (
                                    <div
                                        key={String(row.dimension)}
                                        className={`grid ${d.pageDetail.splitCols} ${d.pageDetail.splitMinW} items-center gap-3 px-5 py-3 border-b last:border-b-0 border-border/60`}
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-[13px] font-semibold text-foreground truncate">{row.label}</span>
                                            <span className={`${d.pageDetail.splitFlag} ${d.tone[row.flag.tone]}`}>
                                                {row.flag.label}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className={d.pageDetail.shareTrack}>
                                                <div
                                                    className={`${d.pageDetail.shareFill} ${row.barClass}`}
                                                    style={{ width: `${maxSplitSpend > 0 ? Math.max(1.5, (row.spend / maxSplitSpend) * 100) : 0}%` }}
                                                />
                                            </div>
                                            <span className={d.pageDetail.shareSpend}>
                                                {formatCurrency(row.spend, currency)} · {Math.round(row.share)}%
                                            </span>
                                        </div>
                                        <div className={`text-right text-sm font-semibold tabular-nums ${row.converted ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground/60'}`}>
                                            {resultIsAction ? formatInt(row.rowResult) : '—'}
                                        </div>
                                        <div className={`text-right text-[13px] tabular-nums ${row.converted ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                                            {row.converted && row.rowResult > 0
                                                ? formatCurrency(row.spend / row.rowResult, currency)
                                                : t('ads.split.noResultShort')}
                                        </div>
                                        <div className={`text-right text-[13px] tabular-nums ${row.ctrLow ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
                                            {row.ctr === null ? '—' : formatPercent(row.ctr)}
                                        </div>
                                    </div>
                                ))}

                                <div className={`grid ${d.pageDetail.splitCols} ${d.pageDetail.splitMinW} items-center gap-3 px-5 py-3 bg-muted/30 border-t border-border`}>
                                    <div className="text-[13px] font-semibold text-foreground">{t('ads.totals.label')}</div>
                                    <div className="text-right text-[13px] font-semibold text-foreground tabular-nums pr-[116px]">
                                        {formatCurrency(splitTotals.spend, currency)}
                                    </div>
                                    <div className="text-right text-sm font-bold text-foreground tabular-nums">
                                        {resultIsAction ? formatInt(splitTotals.results) : '—'}
                                    </div>
                                    <div className="text-right text-[13px] font-semibold text-foreground tabular-nums">
                                        {resultIsAction && splitTotals.results > 0
                                            ? formatCurrency(splitTotals.spend / splitTotals.results, currency)
                                            : '—'}
                                    </div>
                                    <div className="text-right text-[13px] font-semibold text-foreground tabular-nums">
                                        {splitTotalCtr === null ? '—' : formatPercent(splitTotalCtr)}
                                    </div>
                                </div>
                            </div>

                            {(hasAttributionRow || (result.value > 0 && deadSpend > 0)) && (
                                <div className={d.pageDetail.splitFootnote}>
                                    {result.value > 0 && deadSpend > 0 && (
                                        <p>
                                            {t('ads.split.footnoteDead', {
                                                pct: Math.round((deadSpend / (splitTotals.spend || spend || 1)) * 100),
                                                value: formatCurrency(deadSpend, currency),
                                                name: resultName
                                            })}
                                        </p>
                                    )}
                                    {hasAttributionRow && <p>{t('ads.split.footnoteAttribution')}</p>}
                                </div>
                            )}
                        </div>
                    )}

                    {ad.level !== 'ad' && children.length > 0 && (
                        <div className={d.surface.card + ' overflow-hidden'}>
                            <div className="px-5 py-3.5">
                                <h3 className={`${d.pageDetail.cardTitle} mb-0`}>
                                    <Iconify icon="eva:layers-outline" width={18} className="text-violet-600 dark:text-violet-400" />
                                    {ad.level === 'campaign' ? t('ads.detail.adsetsHere') : t('ads.detail.adsHere')}
                                </h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className={d.pageDetail.subTable}>
                                    <thead>
                                        <tr className={d.pageDetail.subHead}>
                                            <th className={d.pageDetail.subHeadCell}>
                                                {ad.level === 'campaign' ? t('ads.table.adset') : t('ads.table.ad')}
                                            </th>
                                            <th className={d.pageDetail.subHeadCellNum}>{t('ads.table.results')}</th>
                                            <th className={d.pageDetail.subHeadCellNum}>{t('ads.table.costPerResult')}</th>
                                            <th className={d.pageDetail.subHeadCellNum}>{t('ads.table.spend')}</th>
                                            <th className={d.pageDetail.subHeadCellNum}>{t('ads.table.budget')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {children.map((child) => {
                                            const childResult = adResult(child);
                                            const childCpr = costPerResult(child, childResult);
                                            const childBudget = budgetInfo(child, currency, t);
                                            const childStatus = statusInfo(child.effective_status || child.status, t);
                                            return (
                                                <tr
                                                    key={child._id}
                                                    className="border-b last:border-b-0 border-border/60 hover:bg-accent/40 transition-colors cursor-pointer"
                                                    onClick={() => navigate(`${BASE_URL}/${child._id}`)}
                                                >
                                                    <td className={d.pageDetail.subCell}>
                                                        <div className="font-semibold truncate max-w-[260px]">
                                                            {child.name || t('ads.table.noName')}
                                                        </div>
                                                        <div className="mt-1">
                                                            <StatusChip tone={childStatus.tone} label={childStatus.label} size="sm" />
                                                        </div>
                                                    </td>
                                                    <td className={d.pageDetail.subCellNum}>
                                                        {childResult.value !== null ? formatInt(childResult.value) : '—'}
                                                    </td>
                                                    <td className={d.pageDetail.subCellNum}>
                                                        {childCpr !== null ? formatCurrency(childCpr, currency) : '—'}
                                                    </td>
                                                    <td className={d.pageDetail.subCellNum}>
                                                        {formatCurrency(child.insights?.spend, currency)}
                                                    </td>
                                                    <td className={`${d.pageDetail.subCellNum} ${childBudget.inherited ? 'text-muted-foreground' : ''}`}>
                                                        {childBudget.value}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    {ad.creative && (ad.creative.body || ad.creative.title || ad.creative.thumbnail_url) && (
                        <div className={d.pageDetail.panel + ' pb-4'}>
                            <div className={d.pageDetail.panelTitle}>{t('ads.detail.creativeLive')}</div>
                            <div className="flex flex-col gap-3">
                                {(ad.creative.thumbnail_url || ad.creative.image_url) && (
                                    <img
                                        src={ad.creative.thumbnail_url || ad.creative.image_url}
                                        alt=""
                                        className="w-full h-40 rounded-xl object-cover border border-border"
                                    />
                                )}
                                <div className="min-w-0 space-y-1">
                                    {ad.creative.title && <p className="text-sm font-semibold text-foreground">{ad.creative.title}</p>}
                                    {ad.creative.body && <p className="text-[13px] leading-relaxed text-muted-foreground whitespace-pre-wrap">{ad.creative.body}</p>}
                                    {ad.creative.call_to_action_type && (
                                        <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-500/25 px-1.5 py-0.5 rounded">
                                            {ad.creative.call_to_action_type}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {panels.map((panel) => (
                        <div key={panel.title} className={d.pageDetail.panel}>
                            <div className={d.pageDetail.panelTitle}>{panel.title}</div>
                            {panel.items.map((item) => (
                                <div key={item.k} className={d.pageDetail.kvRow}>
                                    <span className={d.pageDetail.kvKey}>{item.k}</span>
                                    <span className={panel.muted ? d.pageDetail.kvValueMuted : d.pageDetail.kvValue}>
                                        {item.v}
                                    </span>
                                </div>
                            ))}
                            <div className="h-2" />
                        </div>
                    ))}

                    <div className={d.pageDetail.panel + ' pb-4'}>
                        <div className={d.pageDetail.panelTitle}>{t('ads.syncCard.title')}</div>
                        <p className="text-[13px] text-foreground">
                            {t('ads.syncCard.dataOf', {
                                when: ad.synced_at ? new Date(ad.synced_at).toLocaleString('pt-BR') : '—'
                            })}
                        </p>
                        <p className="text-xs leading-relaxed text-muted-foreground mt-1.5">
                            {t('ads.syncCard.note')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
